import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import Grid from '@mui/material/Grid';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { styled, useTheme } from '@mui/material/styles';
import { useAuth } from '../../../auth';
import { apiClient } from '../../../api-client';

const VACATION_CALENDAR_TYPE = 'vacation';
const OUT_OF_OFFICE_TYPE = 'out-of-office';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date, options) => startOfWeek(date, { ...options, locale: enUS }),
  getDay,
  locales,
});

export const CalendarWrapper = styled('div')(({ theme }) => ({
  '& .rbc-calendar': {
    boxShadow: '0px 5px 16px 0px rgba(0,0,0,0.15);',
  },
  '& .rbc-toolbar': {
    padding: '15px 25px',
  },
  '& .rbc-header': {
    borderLeft: '0px',
    padding: '12px 6px',
    fontWeight: 500,
    fontSize: 14,
  },
  '& .rbc-row .rbc-month-header': {
    border: 'none',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-label': {
    padding: '8px 6px',
  },
  '& .rbc-header.rbc-today, & .rbc-month-view .rbc-day-bg.rbc-today': {
    borderBottom: `2px solid ${theme.palette.danger.main}!important`,
  },
  '& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view': {
    padding: 24,
    [theme.breakpoints.down('md')]: {
      padding: 16,
    },
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-agenda-view table': {
    border: `1px solid ${theme.palette.secondary.main}`,
    '& thead > tr > th': {
      borderBottom: `0px solid ${theme.palette.secondary.main}`,
    },
    '& tbody > tr > td': {
      padding: '12px 6px',
      '& + td': {
        borderLeft: `1px solid ${theme.palette.secondary.main}`,
      },
    },
    '& tbody': {
      color: '#fff',
    },
  },
  '& .rbc-time-view': {
    '& .rbc-time-header': {
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    '& .rbc-time-content': {
      flex: '0 1 auto',
      border: `1px solid ${theme.palette.secondary.main}`,
    },
  },
  '& .rbc-month-view': {
    border: 'none !important',
    '& > .rbc-row': {
      borderBottom: `1px solid ${theme.palette.secondary.main}`,
    },
    '& .rbc-month-row': {
      border: `1px solid ${theme.palette.secondary.main}`,
      borderWidth: '0 1px 1px 1px!important',
      minHeight: 95,
    },
    '& .rbc-header': {
      borderBottom: `0px solid ${theme.palette.secondary.main}`,
    },
    '& .rbc-day-bg + .rbc-day-bg': {
      borderLeft: `1px solid ${theme.palette.secondary.main}`,
    },
  },
  '& .rbc-day-slot .rbc-time-slot': {
    borderTop: `1px solid ${theme.palette.secondary.main}`,
    opacity: 0.5,
  },
  '& .rbc-time-header > .rbc-row > * + *': {
    borderLeft: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-time-content > * + * > *': {
    borderLeft: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-day-bg + .rbc-day-bg': {
    borderLeft: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-time-header > .rbc-row:first-child': {
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-timeslot-group': {
    minHeight: 64,
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
  },
  '& .rbc-date-cell': {
    padding: '6px',
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 400,
    opacity: 0.5,
    '& > a': {
      color: 'inherit',
    },
  },
  '& .rbc-btn-group': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .rbc-toolbar-label': {
    ...theme.typography.body2,
    fontWeight: 600,
  },
  '& .rbc-today': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .rbc-event': {
    fontSize: 12,
    borderRadius: 4,
    padding: '2px 15px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: theme.shadows[0],
    transitionProperty: 'box-shadow',
    transitionDuration: theme.transitions.duration.short,
    transitionTimingFunction: theme.transitions.easing.easeInOut,
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
    },
  },
  '& .rbc-row-segment': {
    padding: '0 4px 4px 4px',
  },
  '& .rbc-off-range-bg': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.16)',
  },
  '& .rbc-show-more': {
    color: theme.palette.secondary.main,
    background: 'transparent',
  },
  '& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event': {
    position: 'static',
  },
  '& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event .rbc-addons-dnd-resize-month-event-anchor:first-child':
    {
      left: 0,
      top: 0,
      bottom: 0,
      height: 'auto',
    },
  '& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event .rbc-addons-dnd-resize-month-event-anchor:last-child':
    {
      right: 0,
      top: 0,
      bottom: 0,
      height: 'auto',
    },
}));

const VacationCalendar = ({ newOutOfOffices }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [myEventsList, setMyEventsList] = useState([]);
  const { user, getToken } = useAuth();

  const addOutOfOfficeEventOptimistically = event => {
    const newEvent = {
      id: 'randomid',
      start: new Date(event.startDate.toISOString()),
      end: new Date(event.endDate.toISOString()),
      title: `${event.requestType}: ${user.displayName}`,
      type: 'out-of-office',
    };
    setMyEventsList([...myEventsList, newEvent]);
  };

  useEffect(() => {
    if (newOutOfOffices) {
      addOutOfOfficeEventOptimistically(newOutOfOffices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOutOfOffices]);

  const handleLoadEvents = async () => {
    try {
      const url = `/calendar/events?calendar=vacation&calendar=out-of-office&tenantId=${user.tenant.id}`;
      const res = await apiClient('GET', url, getToken);
      const allEvents = [...res.data.vacation, ...res.data.outOfOffice].map(e => ({
        ...e,
        start: new Date(e.start),
        end: new Date(e.end),
      }));

      setMyEventsList(allEvents);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err.message || 'something went wrong', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  };

  useEffect(() => {
    handleLoadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventStyleGetter = event => {
    let backgroundColor;

    if (event.type === VACATION_CALENDAR_TYPE) {
      backgroundColor = theme.palette.secondary.dark;
    } else if (event.type === OUT_OF_OFFICE_TYPE) {
      backgroundColor = theme.palette.danger.dark;
    }

    return { style: { backgroundColor } };
  };

  return (
    <CalendarWrapper sx={{ opacity: myEventsList.length === 0 ? 0.5 : 1 }}>
      <Grid
        size={{
          md: 12,
          xl: 12,
          xs: 12,
        }}
      >
        <Calendar
          style={{
            boxShadow: theme.custom.shadows.paper,
            background: 'white',
            border: `none`,
            padding: 0,
          }}
          onDrillDown={null}
          drilldownView={null}
          localizer={localizer}
          events={myEventsList || []}
          startAccessor="start"
          endAccessor="end"
          popup
          eventPropGetter={eventStyleGetter}
        />
      </Grid>
    </CalendarWrapper>
  );
};

export default VacationCalendar;
